import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { HeaderComponent } from '@app/shared/components/header/header.component';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { NumericTextBoxComponent, SwitchComponent } from '@progress/kendo-angular-inputs';
import { ProjectModuleWellConnectSurfaceEquipment } from '../../models/project-module-well-connect-surface-equipment';
import { ProjectModuleService } from '../../services/project-module.service';

@Component({
  selector: 'app-well-connect-surface-equipment',
  templateUrl: './well-connect-surface-equipment.component.html',
  styleUrls: ['./well-connect-surface-equipment.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    ComboBoxComponent,
    FormsModule,
    HeaderComponent,
    NumericTextBoxComponent,
    ReactiveFormsModule,
    SwitchComponent,
  ],
})
export class WellConnectSurfaceEquipmentComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private _projectModuleService = inject(ProjectModuleService);

  readonly xProjectId = input<number>(undefined);

  // emitter tabTdv provides touched-dirty-valid status of the form in this tab
  readonly tabTdv = output<boolean>();

  private thisProjectModuleId: number;
  public decimalPrecision: number = 0;
  public decimalFormat: string = 'n0';
  public percentFormat: string = 'p0';

  private projectModuleTableName: string = 'ProjectModuleWellConnectSurfaceEquipments';

  public errorMessage: string;
  public isFormNew: boolean;
  public isProjectModuleReadError: boolean = false;
  public shouldDisplaySeparators: boolean;

  public projectModuleFormSE: UntypedFormGroup;
  public projectModule: ProjectModuleWellConnectSurfaceEquipment;
  public projectModuleCount: number;
  public xModule: ProjectModuleWellConnectSurfaceEquipment;
  public yModule: ProjectModuleWellConnectSurfaceEquipment;
  public newModule: ProjectModuleWellConnectSurfaceEquipment;
  public modifyModule: ProjectModuleWellConnectSurfaceEquipment;

  public bulkDiameterData: Array<string> = ['24 inch', '32 inch', '56 inch'];

  ngOnInit() {
    this.generateForm();
    this.getProjectModule(this.xProjectId());
    this.shouldDisplaySeparators = true;
  }

  public getProjectModule(v: number): void {
    this._projectModuleService.get(v, this.projectModuleTableName)
      .then(payload => {
        this.projectModule = payload.value;
        this.projectModuleCount = payload['@odata.count'];
        if (this.projectModuleCount === 0) {
          this.isFormNew = true;
        } else {
          if (this.projectModuleCount === 1) {
            this.isFormNew = false;
            this.thisProjectModuleId = this.projectModule[0].ProjectModuleWellConnectSurfaceEquipmentId;
            this.populateForm(payload.value[0]);
          } else {
            if (this.projectModuleCount > 1) {
              this.isFormNew = false;
              this.thisProjectModuleId = this.projectModule[0].ProjectModuleWellConnectSurfaceEquipmentId;
              this.populateForm(payload.value[0]);
              this.isProjectModuleReadError = true;
              console.log('multiple records found with identical Project IDs');
            }
          }
        }
      });
  }

  private populateForm(data): void {
    this.projectModuleFormSE.patchValue({
      BulkSeparatorDiameter: data.BulkSeparatorDiameter,
      TestAllocationOfAllWells: data.TestAllocationOfAllWells,
      QuantityOfPermanentSeparators: data.QuantityOfPermanentSeparators,
      QuantityOfRentalSeparators: data.QuantityOfRentalSeparators,
      IsPower: data.IsPower,
      PowerLineLength: data.PowerLineLength,
      IsScada: data.IsScada,
      IsWellHeadConnect: data.IsWellHeadConnect,
      InletHeadertoWellLength: data.InletHeadertoWellLength,
      UseExistingBulkSeparator: data.UseExistingBulkSeparator
    });
  }

  private generateForm(): void {
    this.projectModuleFormSE = this.fb.group({
      BulkSeparatorDiameter: [null, [Validators.required]],
      TestAllocationOfAllWells: [false],
      QuantityOfPermanentSeparators: [null, [Validators.required]],
      QuantityOfRentalSeparators: [null, [Validators.required]],
      IsPower: [false],
      PowerLineLength: [null, [Validators.required]],
      IsScada: [false],
      IsWellHeadConnect: [false],
      InletHeadertoWellLength: [null, [Validators.required]],
      UseExistingBulkSeparator: [false]
    });

    this.projectModuleFormSE.valueChanges.subscribe(data => this.onValueChanged(data));
  }

  onValueChanged(data?: any) {
    if (!this.projectModuleFormSE) {
      return;
    }
    const form = this.projectModuleFormSE;
    let isValid: boolean = false;
    if (this.projectModuleFormSE.valid) {
      isValid = true;
      this.setTDV();
    } else {
      isValid = false;
      this.clearTDV();
    }
  }

  public updateProjectModule(e) {
    this.modifyModule = {
      Description: 'Surface Equipment',
      BulkSeparatorDiameter: this.projectModuleFormSE.controls.BulkSeparatorDiameter.value,
      TestAllocationOfAllWells: this.projectModuleFormSE.controls.TestAllocationOfAllWells.value,
      QuantityOfPermanentSeparators: this.projectModuleFormSE.controls.QuantityOfPermanentSeparators.value,
      QuantityOfRentalSeparators: this.projectModuleFormSE.controls.QuantityOfRentalSeparators.value,
      IsPower: this.projectModuleFormSE.controls.IsPower.value,
      PowerLineLength: this.projectModuleFormSE.controls.PowerLineLength.value,
      IsScada: this.projectModuleFormSE.controls.IsScada.value,
      IsWellHeadConnect: this.projectModuleFormSE.controls.IsWellHeadConnect.value,
      InletHeadertoWellLength: this.projectModuleFormSE.controls.InletHeadertoWellLength.value,
      UseExistingBulkSeparator: this.projectModuleFormSE.controls.UseExistingBulkSeparator.value
    };
    //  Get PATCH request in form that's usable with the api
    let Form = JSON.stringify(this.modifyModule);

    this._projectModuleService.update(this.thisProjectModuleId, Form, this.projectModuleTableName)
      .then(() => {
        this.onSaveComplete(this.xProjectId());
      });
  }

  public createProjectModule(e): void {
    this.newModule = {
      ProjectId: this.xProjectId(),
      Description: 'Surface Equipment',
      BulkSeparatorDiameter: this.projectModuleFormSE.controls.BulkSeparatorDiameter.value,
      TestAllocationOfAllWells: this.projectModuleFormSE.controls.TestAllocationOfAllWells.value ? this.projectModuleFormSE.controls.TestAllocationOfAllWells.value : false,
      QuantityOfPermanentSeparators: this.projectModuleFormSE.controls.QuantityOfPermanentSeparators.value,
      QuantityOfRentalSeparators: this.projectModuleFormSE.controls.QuantityOfRentalSeparators.value,
      IsPower: this.projectModuleFormSE.controls.IsPower.value ? this.projectModuleFormSE.controls.IsPower.value : false,
      PowerLineLength: this.projectModuleFormSE.controls.PowerLineLength.value,
      IsScada: this.projectModuleFormSE.controls.IsScada.value ? this.projectModuleFormSE.controls.IsScada.value : false,
      IsWellHeadConnect: this.projectModuleFormSE.controls.IsWellHeadConnect.value ? this.projectModuleFormSE.controls.IsWellHeadConnect.value : false,
      InletHeadertoWellLength: this.projectModuleFormSE.controls.InletHeadertoWellLength.value,
      UseExistingBulkSeparator: this.projectModuleFormSE.controls.UseExistingBulkSeparator.value
    };

    //  Put POST request in form that's usable with the api
    let Form = JSON.stringify(this.newModule);

    this._projectModuleService.create(Form, this.projectModuleTableName)
      .then((data: any) => {
        this.onSaveComplete(data.ProjectId);
      });
  }

  onSaveComplete(v: number): void {
    this.isFormNew = false;
    this.getProjectModule(v);
  }

  public handleSeparators(e): void {
    this.shouldDisplaySeparators = !e;
  }

  public setTDV(): void {
    let tdvState: boolean = true;
    this.tabTdv.emit(tdvState);
  }

  public clearTDV(): void {
    let tdvState: boolean = false;
    this.tabTdv.emit(tdvState);
  }
}